import { PROMPT_CACHE_MODES, type CacheReport, type ProviderType } from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatCompact, formatDateTime, formatHitRate, formatPercent } from '@/lib/format';

const RANGES = { '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days' } as const;
type Range = keyof typeof RANGES;

const number = new Intl.NumberFormat();
const MODE_LABEL = {
  explicit: 'Breakpoints',
  automatic: 'Automatic',
  server: 'Server-dependent',
  managed: 'Managed by SDK',
} as const;

const ratio = (read: number, input: number) => (input > 0 ? Math.min(1, read / input) : 0);

/** One bar per day: how much of the input came from the cache. */
function DailyHitRate({ daily }: { daily: CacheReport['daily'] }) {
  return (
    <div className="grid gap-2" role="img" aria-label="Cache hit rate per day">
      <div className="flex h-32 items-end gap-1.5 border-b">
        {daily.map((d) => {
          const r = ratio(d.cacheReadTokens, d.inputTokens);
          return (
            <div
              key={d.day}
              className="group relative flex h-full min-w-1 flex-1 items-end"
              title={`${d.day}: ${formatHitRate(d.cacheReadTokens, d.inputTokens)} hit rate · ${number.format(d.cacheReadTokens)} of ${number.format(d.inputTokens)} input tokens cached · ${d.runs} runs`}
            >
              <div className="bg-muted absolute inset-0 rounded-t-[3px] opacity-60" aria-hidden />
              <div
                className="bg-tint-blue relative w-full rounded-t-[3px] transition-[height] duration-300"
                style={{ height: `${Math.max(r * 100, r > 0 ? 3 : 0)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="text-muted-foreground flex justify-between text-xs tabular-nums">
        <span>{daily[0]?.day}</span>
        <span>{daily.at(-1)?.day}</span>
      </div>
    </div>
  );
}

function CachingContent({ tenantId }: { tenantId: string }) {
  const [range, setRange] = useState<Range>('30');
  const report = useQuery({
    queryKey: ['caching', tenantId, range],
    queryFn: () => {
      // Whole UTC days, so the window does not shift on every refetch.
      const from = new Date();
      from.setUTCHours(0, 0, 0, 0);
      from.setUTCDate(from.getUTCDate() - Number(range) + 1);
      return api<CacheReport>(`/caching?${new URLSearchParams({ from: from.toISOString() })}`);
    },
  });
  const data = report.data;
  const t = data?.totals;

  return (
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <Label htmlFor="cache-range" className="text-muted-foreground text-xs">
          Period
        </Label>
        <Select value={range} onValueChange={(v) => setRange(v as Range)}>
          <SelectTrigger id="cache-range" aria-label="Period" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(RANGES).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ErrorAlert error={report.error} />
      {report.isPending || !data || !t ? (
        <Skeleton className="h-64" />
      ) : t.runs === 0 ? (
        <EmptyState title="No runs in this period">
          Cache numbers appear once agents have run with token usage recorded.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              [
                'Cache hit rate',
                t.cacheReadTokens + t.cacheWriteTokens > 0
                  ? formatHitRate(t.cacheReadTokens, t.inputTokens)
                  : '—',
                `${number.format(t.cacheReadTokens)} of ${number.format(t.inputTokens)} input tokens read from cache`,
              ],
              [
                'Cached tokens',
                formatCompact(t.cacheReadTokens),
                `${number.format(t.cacheReadTokens)} tokens billed at the cache-read price`,
              ],
              [
                'Runs with a hit',
                `${number.format(t.runsWithHit)} / ${number.format(t.runs)}`,
                `${formatPercent(t.runs > 0 ? t.runsWithHit / t.runs : 0)} of runs read something from the cache`,
              ],
              [
                'Cache writes',
                formatCompact(t.cacheWriteTokens),
                'Tokens written to the cache (Anthropic bills these at a premium)',
              ],
            ].map(([label, value, title]) => (
              <Card key={label}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-normal">
                    {label}
                  </CardTitle>
                </CardHeader>
                <CardContent
                  className="text-2xl font-semibold tabular-nums"
                  title={title}
                  data-testid={`cache-total-${label}`}
                >
                  {value}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hit rate by day</CardTitle>
            </CardHeader>
            <CardContent>
              <DailyHitRate daily={data.daily} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By model</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    <TableHead>Caching</TableHead>
                    <TableHead className="text-right">Runs</TableHead>
                    <TableHead className="text-right">Input</TableHead>
                    <TableHead className="text-right">Cached</TableHead>
                    <TableHead className="text-right">Hit rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.models.map((m) => {
                    const info = PROMPT_CACHE_MODES[m.providerType as ProviderType];
                    return (
                      <TableRow key={`${m.providerType}/${m.model}`}>
                        <TableCell>
                          <div className="font-mono text-xs">{m.model}</div>
                          <div className="text-muted-foreground text-xs">{m.providerType}</div>
                        </TableCell>
                        <TableCell>
                          <span title={info.note}>
                            <Badge variant="secondary">{MODE_LABEL[info.mode]}</Badge>
                          </span>
                          {m.cacheReadTokens === 0 && (
                            <div className="text-muted-foreground mt-1 max-w-64 text-xs">
                              {info.note}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {number.format(m.runs)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCompact(m.inputTokens)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCompact(m.cacheReadTokens)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {m.cacheReadTokens + m.cacheWriteTokens > 0
                            ? formatHitRate(m.cacheReadTokens, m.inputTokens)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent runs</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="text-right">Input</TableHead>
                    <TableHead className="text-right">Cached</TableHead>
                    <TableHead className="text-right">Hit rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        <Link
                          to="/traces/runs/$runId"
                          params={{ runId: r.id }}
                          className="underline decoration-dotted underline-offset-4"
                        >
                          {formatDateTime(r.startedAt)}
                        </Link>
                      </TableCell>
                      <TableCell>{r.agentName ?? 'Deleted agent'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.model}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCompact(r.inputTokens)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.cacheReadTokens > 0 ? formatCompact(r.cacheReadTokens) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.cacheReadTokens > 0 ? (
                          <Badge variant="secondary">
                            {formatHitRate(r.cacheReadTokens, r.inputTokens)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">miss</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export function CachingPage() {
  return (
    <>
      <PageHeader
        title="Prompt caching"
        description="How much of each agent's prompt is served from the provider's cache (times in UTC)."
      />
      <RequireTenant>{(tenant) => <CachingContent tenantId={tenant.id} />}</RequireTenant>
    </>
  );
}
